import { z } from 'zod';
import {
  catalog,
  featureCodes,
  FeatureCode as CatalogFeatureCode,
  SectionInputFor,
} from '@playbook-technologies/common-schemas/lib/catalog';
import { ISection } from './schemas';
import {
  requiredString,
  optionalObject,
  optionalBoolean,
  optionalString,
  optionalNumber,
  optionalObjectArray,
  optionalArray,
  emptyRule,
} from './zod-helpers';
import { FeatureCode } from '../../../generated/graphql';

const sharedFieldsSchema = <T extends string>(v: T) => ({
  type: z.literal(v),
  enabled: z.literal(true),
  bgColor: requiredString('Background Color'),
  colorIndex: optionalNumber(),
});

export const disabledSectionTypePostfix = '-disabled';
const optionalSection = <
  T extends z.ZodRawShape,
  TName extends SectionInputFor<'purchase' | 'offer'>['type'],
>(
  type: TName,
  schema: z.ZodObject<T>,
) =>
  [
    schema.extend(sharedFieldsSchema(type)).strict(),
    z
      .object({
        type: z.literal(`${type}${disabledSectionTypePostfix}`),
        enabled: z.literal(false),
      })
      .passthrough(),
  ] satisfies [z.AnyZodObject, z.AnyZodObject];

const INTRO_TEXT_LABEL = 'Intro text';

const ctaSchema = optionalObject({
  enabled: optionalBoolean(),
  customize: optionalBoolean(),
  text: optionalString(),
  link: optionalString(),
});

const introductionSectionSchema = catalog.Introduction.schema
  .omit({ bullets: true })
  .extend({
    ...sharedFieldsSchema('Introduction'),
    title: requiredString('Title', 60),
    subtitle: requiredString('Subtitle', 60),
    desc: emptyRule(
      z.string().max(500, {
        message: `${INTRO_TEXT_LABEL} is too long`,
      }),
    ),
    coverImage: requiredString('Cover image'),
    primaryColor: requiredString('Primary Color'),
    introVideo: optionalString(),
    logo: optionalString(),
    priceBefore: optionalNumber(),
    cta: ctaSchema,
  });

export const emailCaptureSectionSchema = optionalSection(
  'EmailCapture',
  catalog.EmailCapture.schema.extend({
    title: requiredString('Title', 60),
    description: requiredString('Description', 240),
    image: optionalString(),
    displayAsPopup: optionalBoolean(),
  }),
);

const programStatsSectionSchema = optionalSection(
  'ProgramStats',
  catalog.ProgramStats.schema,
);

const reviewsSections = optionalSection(
  'Reviews',
  catalog.Reviews.schema.extend({
    title: optionalString(),
    reviews: optionalObjectArray({
      name: requiredString('Name'),
      text: requiredString('Text'),
    }),
  }),
);

const textSectionSharedFields = {
  title: requiredString('Title', 60),
  desc: requiredString('Description'),
  bullets: optionalObjectArray({ text: requiredString('Bullet Text') }),
  cta: ctaSchema,
};

const textSection1Schema = optionalSection(
  'TextSection1',
  catalog.TextSection1.schema.omit({ swapSides: true }).extend({
    ...textSectionSharedFields,
    coverImage: requiredString('Cover image'),
    introVideo: optionalString(),
  }),
);

const textSection2Schema = optionalSection(
  'TextSection2',
  catalog.TextSection2.schema.omit({ swapSides: true }).extend({
    ...textSectionSharedFields,
    images: z
      .array(z.object({ url: z.string().nullable().default(null) }))
      .optional()
      .default([]),
    rotateImages: optionalBoolean(),
  }),
);

const featuresSectionSchema = optionalSection(
  'Features',
  catalog.Features.schema.extend({
    title: optionalString(),
    remindersCoverImage: optionalString(),
    streaksCoverImage: optionalString(),
    workoutsCoverImage: optionalString(),
    favoritesCoverImage: optionalString(),
    lifetimeCoverImage: optionalString(),
    streamCoverImage: optionalString(),
  }),
);

const featureType = z.enum(featureCodes).exclude(['messages', 'community']);
const requiredFeatureType = (schema: z.ZodType<CatalogFeatureCode>) =>
  schema.or(z.undefined()).refine((value) => value !== undefined, {
    message: 'Feature Type is required',
  });

const otpFeaturesSectionSchema = optionalSection(
  'OTPFeatures',
  catalog.OTPFeatures.schema.extend({
    title: optionalString(),
    features: optionalObjectArray({
      type: requiredFeatureType(featureType),
      name: requiredString('Feature Name'),
      image: requiredString('Feature Image'),
    }),
  }),
);

const featuresNewSectionSchema = optionalSection(
  'FeaturesNew',
  catalog.FeaturesNew.schema.extend({
    title: optionalString(),
    features: z
      .array(
        z.object({
          type: requiredFeatureType(z.enum(featureCodes)),
          name: requiredString('Feature Name'),
          image: requiredString('Feature Image'),
        }),
      )
      .length(6, { message: 'Select 6 features' }),
  }),
);

const workoutsSectionSchema = optionalSection(
  'Workouts',
  catalog.Workouts.schema.extend({
    title: optionalString(),
    workoutIds: optionalArray(z.number()),
  }),
);

export const offerDetailsSectionSchema = optionalSection(
  'OfferDetails',
  catalog.OfferDetails.schema.extend({
    headlineText: requiredString('Headline text', 60),
    programIds: optionalArray(z.number()),
    workoutsPreviewEnabled: optionalBoolean(),
    workoutIds: optionalArray(z.number()),
    extrasIds: optionalArray(z.string()),
    communityFeatures: optionalArray(z.nativeEnum(FeatureCode)),
  }),
);

const bioSectionSchema = optionalSection(
  'Bio',
  catalog.Bio.schema.extend({
    title: requiredString('Title'),
    desc: requiredString('Description'),
    coverImage: requiredString('Cover image'),
    introVideo: optionalString(),
    swapSides: optionalBoolean(),
  }),
);

const testimonialsSectionSchema = optionalSection(
  'Testimonials',
  catalog.Testimonials.schema.extend({
    title: optionalString(),
    testimonials: optionalObjectArray({
      video: optionalString(),
      image: optionalString(),
      name: optionalString(),
      text: optionalString(),
    }),
  }),
);

const faqsSectionSchema = optionalSection(
  'FAQs',
  catalog.FAQs.schema.extend({
    title: optionalString(),
    faqs: optionalObjectArray({
      question: requiredString('Question'),
      answer: requiredString('Answer'),
    }),
  }),
);

const sectionSchema = z.discriminatedUnion('type', [
  introductionSectionSchema,
  ...emailCaptureSectionSchema,
  ...programStatsSectionSchema,
  ...reviewsSections,
  ...textSection1Schema,
  ...textSection2Schema,
  ...featuresSectionSchema,
  ...otpFeaturesSectionSchema,
  ...featuresNewSectionSchema,
  ...workoutsSectionSchema,
  ...offerDetailsSectionSchema,
  ...bioSectionSchema,
  ...testimonialsSectionSchema,
  ...faqsSectionSchema,
]);

export const OTPLandingPageSchema = z
  .object({
    sections: z.array(sectionSchema),
  })
  .superRefine((data, ctx) => {
    data.sections.forEach((section, i) => {
      if (
        section.type === 'OfferDetails' &&
        section.workoutsPreviewEnabled &&
        (section.workoutIds?.length ?? 0) === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sections', i, 'workoutIds'],
          message:
            'Select at least one workout or disable the workouts preview.',
        });
      }
    });
  });

export type IntroductionSectionSchema = Extract<
  ISection,
  { type?: 'Introduction' }
>;
export type EmailCaptureSectionSchema = Extract<
  ISection,
  { type?: 'EmailCapture' }
>;
export type OTPFeaturesSectionSchema = Extract<
  ISection,
  { type?: 'OTPFeatures' }
>;
export type ReviewsSectionSchema = Extract<ISection, { type?: 'Reviews' }>;
export type FeaturesSectionSchema = Extract<ISection, { type?: 'Features' }>;
export type OfferDetailsSectionSchema = Extract<
  ISection,
  { type?: 'OfferDetails' }
>;
export type TestimonialsSectionSchema = Extract<
  ISection,
  { type?: 'Testimonials' }
>;
export type FaqsSectionSchema = Extract<ISection, { type?: 'FAQs' }>;
export type SectionSchema = ISection;
export type SharedFieldsSchema = Required<
  Pick<IntroductionSectionSchema, 'enabled' | 'bgColor'>
> &
  Pick<IntroductionSectionSchema, 'colorIndex'>;
